import {View} from '@fantasmagames/slot/src/common/view/View';
import {Position, SymbolPosition} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {SpineAnimation} from '@fantasmagames/core/src/display/SpineAnimation';
import {Tween} from '@fantasmagames/core/src/tween/Tween';
import {GameReelConstants} from 'src/reels/GameReelConstants';
import {RespinScatterInfo} from '@fantasmagames/slot/src/logic/SpinResult';
import Container = PIXI.Container;

export interface RespinData {
	currentScatterPosition: SymbolPosition;
	replaceSymbolsPositions: SymbolPosition[];
	currentRespinSymbolIndex: number;
}

export class RespinView extends View {
	public static readonly RESPIN_END_ANIM_TRIGGERED: string = 'RESPIN_END_ANIM_TRIGGERED';
	public static readonly SYMBOL_WAS_SHOT: string = 'SYMBOL_WAS_SHOT';
	public static readonly LEAVING_RESPIN_SYMBOL: string = 'LEAVING_RESPIN_SYMBOL';
	public static readonly FINISHED: string = 'FINISHED';

	protected _respinsData: RespinData[] = [];
	protected _currentRespinData: RespinData;
	protected _bounceTweenDuration: number = 150;

	public tryToStartRespinSymbols(respinScatters: RespinScatterInfo[]): void {
		this._respinsData = [];

		if (respinScatters.length > 0) {
			for (const respin of respinScatters) {
				this.initReplacePositions(respin);
			}

			this.startRespin();
		}
	}

	protected startRespin(): void {
		this._currentRespinData = this._respinsData.shift();
		const scatterPos = this._currentRespinData.currentScatterPosition;
		this.emitDelayed(RespinView.LEAVING_RESPIN_SYMBOL, scatterPos);
		if (this._currentRespinData.replaceSymbolsPositions.length > 1) {
			this.playStartAnimationAt(scatterPos);
		} else {
			this.playOneSymbolRespin();
		}
	}

	protected playOneSymbolRespin(): void {
		const startPos: Position = this._currentRespinData.replaceSymbolsPositions[0];

		this.playSymbolRemoveAnimation(startPos);
		this.playEndAnimation(startPos, true);
		this.emitDelayed(RespinView.SYMBOL_WAS_SHOT, startPos);
	}

	protected initReplacePositions(respinScatters: RespinScatterInfo): void {
		const data: RespinData = {
			currentScatterPosition: respinScatters.respinScatterPosition
			, currentRespinSymbolIndex: 0
			, replaceSymbolsPositions: [respinScatters.respinScatterPosition],
		};

		for (const symbol of respinScatters.respinSymbols) {
			data.replaceSymbolsPositions.push(symbol);
		}

		this._respinsData.push(data);
	}

	protected playStartAnimationAt(scatterPos: Position): void {
		const startPos: Position = GameReelConstants.getGameSymbolCenterPosition(scatterPos);
		const endPos: Position = GameReelConstants.getGameSymbolCenterPosition(this.getNextRespinPosition());
		const angle: number = this.getAngle(startPos, endPos);

		const startAnim: SpineAnimation =
			this.spineUtils.startSpineAnimationCreation(`RESPIN.json`, 'START', false)
				.container(this.container)
				.onComplete(() => {
					this.playSymbolRemoveAnimation(startPos);
					this.prepareNextFlight();
					this.spineUtils.stopAndDestroyAnimation(startAnim);
				})
				.position(startPos)
				.rotation(angle)
				.createAndStart();

		this.adjustAnimScale(angle, startAnim.container);
	}

	protected prepareNextFlight(): void {
		if (this._currentRespinData.currentRespinSymbolIndex + 1 < this._currentRespinData.replaceSymbolsPositions.length) {
			this.setFromToReelsPositions(
				this._currentRespinData.replaceSymbolsPositions[this._currentRespinData.currentRespinSymbolIndex],
				this.getNextRespinPosition(),
				this.prepareNextFlight.bind(this),
			);

			// this check made because zero index is scatter itself. Which is emitted earlier
			if (this._currentRespinData.currentRespinSymbolIndex !== 0) {
				this.emitDelayed(RespinView.SYMBOL_WAS_SHOT, this._currentRespinData.replaceSymbolsPositions[this._currentRespinData.currentRespinSymbolIndex]);
			}

			this._currentRespinData.currentRespinSymbolIndex++;
		} else {
			const startPos: Position = this._currentRespinData.replaceSymbolsPositions[this._currentRespinData.currentRespinSymbolIndex];
			const isLast: boolean = this._respinsData.length === 0;

			this.playEndAnimation(startPos, isLast);
			this.emitDelayed(RespinView.SYMBOL_WAS_SHOT, startPos);
		}
	}

	protected getNextRespinPosition(): Position {
		return this._currentRespinData.replaceSymbolsPositions[this._currentRespinData.currentRespinSymbolIndex + 1];
	}

	protected setFromToReelsPositions(fromPos: SymbolPosition, toPos: SymbolPosition, onFinishedCallback: Function): void {
		const fromReelPos: Position = GameReelConstants.getGameSymbolCenterPosition(fromPos);
		const toReelPos: Position = GameReelConstants.getGameSymbolCenterPosition(toPos);
		this.startBounceFromTo(fromReelPos, toReelPos, onFinishedCallback);
	}

	protected startBounceFromTo(startPos: Position, endPos: Position, onFinishedCallback: Function): void {
		const angle: number = this.getAngle(startPos, endPos);

		const bounceAnim: SpineAnimation =
			this.spineUtils.startSpineAnimationCreation(`RESPIN.json`, 'BOUNCE', false)
				.container(this.container)
				.position(startPos)
				.rotation(angle)
				.createAndStart();

		const scale: number = this.getDistanceBetween(startPos, endPos) / this.getBoltLength();
		bounceAnim.container.scale.set(1, scale);

		const tween = new Tween(bounceAnim.container)
			.to({x: endPos.x, y: endPos.y}, this._bounceTweenDuration)
			.onComplete(() => {
				this.playSymbolRemoveAnimation(endPos);
				onFinishedCallback && onFinishedCallback();
				this.spineUtils.stopAndDestroyAnimation(bounceAnim);
			})
			.start();

		this.updateManager.add(tween);
	}

	protected playSymbolRemoveAnimation(pos: Position): void {
		const removeAnim: SpineAnimation =
			this.spineUtils.startSpineAnimationCreation(`SYMBOL_REMOVE.json`, 'SYMBOL_RESPIN_REMOVE', false)
				.container(this.container)
				.onComplete(() => {
					this.spineUtils.stopAndDestroyAnimation(removeAnim);
				})
				.position(pos)
				.createAndStart();
	}

	protected playEndAnimation(pos: Position, isLast: boolean): void {
		const animPos: Position = GameReelConstants.getGameSymbolCenterPosition(pos);

		const endAnim: SpineAnimation =
			this.spineUtils.startSpineAnimationCreation(`RESPIN.json`, 'END', false)
				.container(this.container)
				.onComplete(() => {
					this.playExitAnimation(animPos, isLast);
					this.spineUtils.stopAndDestroyAnimation(endAnim);
				})
				.position(animPos)
				.createAndStart();
	}

	protected playExitAnimation(pos: Position, isLast: boolean): void {
		const exitAnim: SpineAnimation =
			this.spineUtils.startSpineAnimationCreation(`RESPIN.json`, 'EXIT', false)
				.container(this.container)
				.onComplete(() => {
					if (isLast) {
						this._respinsData = [];
						this.emitDelayed(RespinView.FINISHED);
					} else {
						this.startRespin();
					}

					this.spineUtils.stopAndDestroyAnimation(exitAnim);
				})
				.position(pos)
				.createAndStart();

		this.emitDelayed(RespinView.RESPIN_END_ANIM_TRIGGERED);
	}

	protected getAngle(startPos: Position, endPos: Position): number {
		return Math.atan2(endPos.y - startPos.y, endPos.x - startPos.x);
	}

	protected adjustAnimScale(angle: number, animContainer: Container): void {
		if (Math.abs(angle) > 1) {
			animContainer.scale.set(1, -1);
		}
	}

	protected getDistanceBetween(pt1: Position, pt2: Position): number {
		return Math.sqrt(Math.pow(pt2.x - pt1.x, 2) + Math.pow(pt2.y - pt1.y, 2));
	}

	protected getBoltLength(): number {
		return GameReelConstants.getBottomSymbolSlot().width * 5;
	}
}



// WEBPACK FOOTER //
// ./src/respin/RespinView.ts